<template>
  <div id="keyword-detail">
    <div class="page-header">
      <button class="btn-back" @click="$router.back()">← 목록</button>
      <h2 class="page-title">{{ decodedKeyword }}</h2>
    </div>

    <div v-if="loading" class="empty-state">불러오는 중…</div>
    <div v-else-if="errorMessage" class="notice notice-error">{{ errorMessage }}</div>

    <template v-else-if="detail">
      <section class="metric-grid">
        <div class="metric">
          <div class="metric-label">월간 검색량</div>
          <div class="metric-value">{{ detail.totalVolume.toLocaleString() }}</div>
          <div class="metric-sub">
            PC {{ detail.pcVolume.toLocaleString() }} · 모바일
            {{ detail.mobileVolume.toLocaleString() }}
          </div>
        </div>
        <div class="metric">
          <div class="metric-label">월간 발행량</div>
          <div class="metric-value">
            {{ detail.docCount === null ? "-" : detail.docCount.toLocaleString() }}
          </div>
          <div class="metric-sub">블로그 문서 수</div>
        </div>
        <div class="metric">
          <div class="metric-label">포화 지수</div>
          <div class="metric-value" :class="saturationClass">
            {{ detail.saturation === null ? "-" : detail.saturation.toFixed(2) }}
          </div>
          <div class="metric-sub">낮을수록 유리</div>
        </div>
        <div class="metric">
          <div class="metric-label">경쟁정도</div>
          <div class="metric-value">{{ detail.competition || "-" }}</div>
          <div class="metric-sub">네이버 광고 기준</div>
        </div>
      </section>

      <section class="chart-card">
        <div class="chart-title">최근 1년 검색 추이</div>
        <p class="chart-note">
          데이터랩 기준 <b>상대비율</b>입니다(구간 최고점 = 100). 절대 검색량이 아닙니다.
        </p>
        <div class="chart-area">
          <Line v-if="chartData" :data="chartData" :options="chartOptions" class="chart" />
          <div v-else class="chart-skeleton">추이 데이터가 없습니다.</div>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
} from "chart.js";
import type { ChartData, ChartOptions } from "chart.js";
import * as keywordApi from "@/keyword/api";
import type { KeywordDetail } from "@/keyword/api";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler
);

export default defineComponent({
  name: "KeywordDetailView",
  components: { Line },
  props: {
    keyword: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      detail: null as KeywordDetail | null,
    };
  },
  computed: {
    decodedKeyword(): string {
      try {
        return decodeURIComponent(this.keyword);
      } catch {
        return this.keyword;
      }
    },
    saturationClass(): string {
      const s = this.detail?.saturation;
      if (s === null || s === undefined) return "";
      if (s < 1) return "good";
      if (s < 5) return "mid";
      return "bad";
    },
    chartData(): ChartData<"line"> | null {
      if (!this.detail || !this.detail.trend || this.detail.trend.length === 0) {
        return null;
      }
      return {
        labels: this.detail.trend.map((p) => p.period.substring(0, 7)),
        datasets: [
          {
            label: "검색 추이",
            data: this.detail.trend.map((p) => p.ratio),
            borderColor: "#4a9fe0",
            backgroundColor: "rgba(74, 159, 224, 0.15)",
            fill: true,
            tension: 0.4,
            pointRadius: 3,
          },
        ],
      };
    },
    chartOptions(): ChartOptions<"line"> {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, max: 100 } },
      };
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.errorMessage = "";
      try {
        // axiosRequest 는 BaseResponse 봉투를 그대로 돌려준다 → payload 는 res.data
        const res: any = await keywordApi.getDetail(this.decodedKeyword);
        const payload = res?.data;

        if (payload && payload.keyword) {
          this.detail = payload;
        } else if (res?.status === 403) {
          this.errorMessage = "키워드 리서치 사용 권한이 없습니다.";
        } else {
          this.errorMessage =
            res?.message || payload?.message || "조회에 실패했습니다.";
        }
      } catch (e: any) {
        this.errorMessage = e?.message || "조회 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    keyword() {
      this.load();
    },
  },
  created() {
    this.load();
  },
});
</script>

<style lang="scss" scoped></style>
